import { FieldError } from '@/components/FieldError/FieldError';
import cx from 'classnames';
import clsx from 'clsx';
import {
  ChangeEvent,
  ElementRef,
  FocusEvent,
  ForwardRefRenderFunction,
  InputHTMLAttributes,
  forwardRef,
  useState,
} from 'react';

import styles from './TextField.module.scss';

// We want to maintain the value type for TextField
type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value'> & {
  type: 'text' | 'number' | 'email' | 'password';
  label: string;
  value?: string | number | null;
  defaultValue?: string | number;
  name?: string;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  required?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  className?: string;
  containerClassName?: string;
};

const TextField: ForwardRefRenderFunction<ElementRef<'input'>, TextFieldProps> = (
  {
    type,
    label,
    value,
    name,
    error,
    errorMessage,
    defaultValue,
    disabled,
    required,
    onChange,
    onFocus,
    onBlur,
    className,
    containerClassName,
    ...props
  },
  ref,
) => {
  const [isFocused, setIsFocused] = useState(false);
  const isFilled = value !== undefined && value !== null && value !== '';

  return (
    <div className={cx(styles.container, containerClassName)}>
      <input
        ref={ref}
        type={type}
        id={label}
        value={value ?? ''}
        defaultValue={defaultValue}
        name={name}
        onChange={onChange}
        onFocus={e => {
          setIsFocused(true);
          onFocus?.(e);
        }}
        onBlur={e => {
          setIsFocused(false);
          onBlur?.(e);
        }}
        className={cx(className, error && styles.error)}
        disabled={disabled}
        required={required}
        {...props}
      />
      <label
        htmlFor={label}
        className={clsx({
          [styles.focusedLabel]: isFocused,
          [styles.filledLabel]: isFilled,
          [styles.requiredLabel]: required,
        })}
      >
        {label}
      </label>
      {error && <FieldError errorMessage={errorMessage} />}
    </div>
  );
};

const TextFieldWithRef = forwardRef(TextField);
export { TextFieldWithRef as TextField };
export default TextFieldWithRef;
